import { useMsal } from '@azure/msal-react'
import {
  faArrowLeft,
  faEnvelope,
  faExpand,
  faInfoCircle,
  faLocationDot,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Badge,
  Group,
  Image,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { fetchDeviceAdvertisementDetails } from '../../data/advertisement.service'
import { DeviceAdvertisementsForHost } from '../../models/DeviceAdvertisementsForHost'
import AdMetricsPanel from '../../components/AdMetricsPanel'
import { COLORS, defaultImageUrl } from '../../utils/constants'
import { AdStatusTooltip } from '../../components/dashboard/AdStatusTooltip'
import { convertServerDateToLocal } from '../../utils/dateUtils'
import { useSessionInfo } from '../../contexts/SessionContext'
import { useLocalStorage } from '@mantine/hooks'

const AdvertisementDetailsPage = () => {
  const { instance } = useMsal()
  const navigate = useNavigate()
  const { deviceAdId } = useParams<{ deviceAdId: string }>()
  const { session } = useSessionInfo()
  const [isHostMode] = useLocalStorage({
    key: 'isHostMode',
    defaultValue: false,
  })
  const [advertisement, setAdvertisement] =
    useState<DeviceAdvertisementsForHost | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showImageModal, setShowImageModal] = useState(false)
  useEffect(() => {
    const fetchAdvertisementData = async () => {
      if (!deviceAdId) {
        navigate('/dashboard/myAds')
        return
      }

      setIsLoading(true)
      try {
        const data = await fetchDeviceAdvertisementDetails(
          parseInt(deviceAdId),
          instance,
        )
        setAdvertisement(data)
      } catch (error) {
        console.error('Error fetching advertisement:', error)
        notifications.show({
          title: 'Error',
          message: 'Failed to load advertisement details',
          color: 'red',
        })
        navigate('/dashboard/myAds')
      } finally {
        setIsLoading(false)
      }
    }

    fetchAdvertisementData()
  }, [deviceAdId, instance, navigate])

  const handleBack = () => {
    navigate(-1)
  }

  const handleImageExpand = () => {
    setShowImageModal(true)
  }

  if (isLoading) {
    return (
      <div style={{ backgroundColor: 'white', width: '100%', height: '100%' }}>
        <Stack p={24} gap={24}>
          <Group>
            <ActionIcon variant='subtle' size='lg' onClick={handleBack}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </ActionIcon>
            <Skeleton height={32} width={200} />
          </Group>

          <Paper withBorder p='xl' radius='md'>
            <Group align='flex-start' gap='xl'>
              {/* Image Skeleton */}
              <div style={{ position: 'relative' }}>
                <AspectRatio
                  ratio={16 / 9}
                  style={{ width: '100%', maxWidth: 350 }}
                >
                  <Skeleton height='100%' />
                </AspectRatio>
              </div>

              {/* Content Skeleton */}
              <Stack style={{ flex: 1 }} gap='lg'>
                {/* Title and Status Skeleton */}
                <Group justify='space-between' align='center'>
                  <Skeleton height={32} width={250} />
                  <Group gap='md'>
                    <Skeleton height={24} width={80} />
                    <Skeleton height={24} width={100} />
                  </Group>
                </Group>

                {/* Email Contact Skeleton */}
                <Group>
                  <Skeleton height={20} width={150} />
                </Group>

                {/* Location Skeleton */}
                <Group>
                  <Skeleton height={20} width={200} />
                </Group>

                {/* Device Info Skeleton */}
                <Group>
                  <Skeleton height={20} width={300} />
                </Group>

                {/* Additional Details Skeleton */}
                <Stack gap='md' mt='lg'>
                  <Skeleton height={24} width={150} />
                  <Group gap='xl'>
                    <Stack gap={8}>
                      <Skeleton height={16} width={100} />
                      <Skeleton height={16} width={80} />
                    </Stack>
                    <Stack gap={8}>
                      <Skeleton height={16} width={100} />
                      <Skeleton height={16} width={80} />
                    </Stack>
                  </Group>
                </Stack>
              </Stack>
            </Group>
          </Paper>
        </Stack>
      </div>
    )
  }

  if (!advertisement) {
    return (
      <div style={{ backgroundColor: 'white', width: '100%', height: '100%' }}>
        <Stack p={24} gap={24}>
          <Group>
            <ActionIcon variant='subtle' size='lg' onClick={handleBack}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </ActionIcon>
            <Title order={2}>Advertisement Not Found</Title>
          </Group>
          <Paper p={24}>
            <Text>The requested advertisement could not be found.</Text>
          </Paper>
        </Stack>
      </div>
    )
  }

  return (
    <div style={{ backgroundColor: 'white', width: '100%', height: '100%' }}>
      <Stack p={24} gap={24}>
        {/* Header */}
        <Group>
          <ActionIcon variant='subtle' size='lg' onClick={handleBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </ActionIcon>
          <Title order={2}>Advertisement Details</Title>
        </Group>

        {/* Advertisement Details - Expanded AdCard Style */}
        <Paper withBorder p='xl' radius='md'>
          <Group align='flex-start' gap='xl'>
            {/* Image Section */}
            <div style={{ position: 'relative' }}>
              <AspectRatio
                ratio={16 / 9}
                style={{ width: '100%', maxWidth: 350 }}
              >
                <Image
                  src={advertisement.adImageUrl || defaultImageUrl}
                  radius='md'
                  alt={advertisement.organizationName}
                  style={{ cursor: 'pointer' }}
                  onClick={handleImageExpand}
                />
              </AspectRatio>
              {advertisement.adImageUrl && (
                <ActionIcon
                  variant='filled'
                  radius='md'
                  onClick={handleImageExpand}
                  style={{
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FontAwesomeIcon
                    icon={faExpand}
                    style={{ fontSize: '14px', color: 'white' }}
                  />
                </ActionIcon>
              )}
            </div>

            {/* Content Section */}
            <Stack style={{ flex: 1 }} gap='lg'>
              {/* Title and Status */}
              <Group justify='space-between' align='center'>
                {isHostMode ? (
                  <Title order={2}>{advertisement.organizationName}</Title>
                ) : (
                  <Link
                    to={`/locations/${advertisement.locationId}`}
                    style={{ textDecoration: 'none' }}
                  >
                    <Title order={2} c={COLORS.HiddenGemzRed}>
                      {advertisement.locationName}
                    </Title>
                  </Link>
                )}
                <Group gap='md'>
                  <AdStatusTooltip
                    status={advertisement.status}
                    isHostMode={isHostMode}
                  >
                    <Badge
                      color={
                        advertisement.status === 'Running'
                          ? COLORS.LightGreen
                          : 'red'
                      }
                      size='lg'
                    >
                      {advertisement.status}
                    </Badge>
                  </AdStatusTooltip>
                  {advertisement.autoRenew && (
                    <Badge color='blue' size='lg'>
                      Auto Renew
                    </Badge>
                  )}
                </Group>
              </Group>

              {/* Email Contact */}
              <Group>
                <Text
                  component='a'
                  href={`mailto:${
                    advertisement.status === 'PendingDesign' ||
                    advertisement.status === 'PendingAdvertiserApproval' ||
                    advertisement.status === 'PendingContentCheck'
                      ? 'support@hiddengemz.tv'
                      : advertisement.hostEmail
                  }`}
                  c={COLORS.HiddenGemzRed}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    paddingLeft: '4px',
                  }}
                >
                  <FontAwesomeIcon
                    icon={faEnvelope}
                    fixedWidth
                    style={{ color: COLORS.HiddenGemzRed, width: '16px' }}
                  />
                  Email{' '}
                  {advertisement.status === 'PendingDesign' ||
                  advertisement.status === 'PendingAdvertiserApproval' ||
                  advertisement.status === 'PendingContentCheck'
                    ? 'Hidden Gemz'
                    : 'Host'}
                </Text>
              </Group>

              {/* Location row — host view only (advertiser sees location as the title) */}
              {isHostMode && (
                <Group>
                  <Text
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      paddingLeft: '4px',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      fixedWidth
                      style={{ color: COLORS.HiddenGemzRed, width: '16px' }}
                    />
                    <Text
                      component={Link}
                      to={`/locations/${advertisement.locationId}`}
                      style={{
                        color: COLORS.HiddenGemzRed,
                        textDecoration: 'underline',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                      size='sm'
                    >
                      {advertisement.locationName}
                    </Text>
                  </Text>
                </Group>
              )}

              {/* Device Info */}
              <Group>
                <Text
                  fw={500}
                  fz={{ base: 'xs', sm: 'md' }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    paddingLeft: '4px',
                  }}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    fixedWidth
                    style={{ color: '#666', width: '16px' }}
                  />
                  {advertisement.deviceName || 'All Devices'} |{' '}
                  {advertisement.autoRenew ? 'Recurring' : 'Single Run'} |{' '}
                  {advertisement.startDateLocal && advertisement.endDateLocal
                    ? `
                       ${new Date(
                         advertisement.startDateLocal,
                       ).toLocaleDateString()} - ${new Date(
                        advertisement.endDateLocal,
                      ).toLocaleDateString()}`
                    : advertisement.numberOfDaysToRunAd
                    ? `${advertisement.numberOfDaysToRunAd} days`
                    : 'Not specified'}
                </Text>
              </Group>

              {/* Denial Reason */}
              {advertisement.status === 'Denied' &&
                advertisement.denialReason && (
                  <Group>
                    <Text
                      fw={500}
                      fz={{ base: 'xs', sm: 'md' }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        paddingLeft: '4px',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        fixedWidth
                        style={{ color: '#666', width: '16px' }}
                      />
                      Denial Reason: {advertisement.denialReason}
                    </Text>
                  </Group>
                )}

              {/* Auto Renewal Cancellation */}
              {advertisement.autoRenewCancelled &&
                advertisement.autoRenewCancelledDate && (
                  <Group>
                    <Text
                      fw={500}
                      fz={{ base: 'xs', sm: 'md' }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        paddingLeft: '4px',
                      }}
                    >
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        fixedWidth
                        style={{ color: '#666', width: '16px' }}
                      />
                      Auto-renewal cancelled on{' '}
                      {convertServerDateToLocal(
                        advertisement.autoRenewCancelledDate,
                      )?.toLocaleDateString()}
                    </Text>
                  </Group>
                )}

              {/* Additional Details Section */}
              <Stack gap='md' mt='lg'>
                {/* Auto Renewal Details */}
                {advertisement.autoRenew && (
                  <Group gap='xl'>
                    <Stack gap={8}>
                      <Text fw={600}>Auto Renewal Status:</Text>
                      <Text>
                        {advertisement.autoRenewCancelled
                          ? 'Cancelled'
                          : 'Active'}
                      </Text>
                    </Stack>
                    {advertisement.autoRenewCancelledDate && (
                      <Stack gap={8}>
                        <Text fw={600}>Cancelled Date:</Text>
                        <Text>
                          {convertServerDateToLocal(
                            advertisement.autoRenewCancelledDate,
                          )?.toLocaleDateString()}
                        </Text>
                      </Stack>
                    )}
                  </Group>
                )}
              </Stack>
            </Stack>
          </Group>
        </Paper>

        {advertisement &&
          (advertisement.status === 'Running' ||
            advertisement.status === 'Completed' ||
            advertisement.status === 'Disabled') && (
            <AdMetricsPanel
              advertisementId={advertisement.advertisementId}
              qrEnabled={advertisement.qrEnabled ?? false}
            />
          )}
      </Stack>

      {/* Image Modal */}
      <Modal
        opened={showImageModal}
        onClose={() => setShowImageModal(false)}
        size='90%'
        centered
        padding={0}
        withCloseButton={false}
        styles={{
          body: {
            height: '90vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            margin: 0,
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          content: {
            height: '90vh',
            maxHeight: '90vh',
            overflow: 'hidden',
            backgroundColor: 'black',
          },
          inner: {
            padding: 0,
          },
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            backgroundColor: 'black',
          }}
        >
          <ActionIcon
            variant='filled'
            radius='xl'
            onClick={() => setShowImageModal(false)}
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              zIndex: 1,
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </ActionIcon>
          {advertisement && (
            <div
              style={{
                width: '100%',
                height: '100%',
                maxWidth: 'calc(90vh * 16/9)',
                maxHeight: '90vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Image
                src={advertisement.adImageUrl || defaultImageUrl}
                alt={advertisement.organizationName}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            </div>
          )}
        </div>
      </Modal>
    </div>
  )
}

export default AdvertisementDetailsPage
